import React, { Fragment, lazy } from "react";
import { constants } from "@/constants.js";
import ChatbotDialog, { botIcons } from "@/components/dialog/ChatbotDialog.js";
import ChatbotOpenBubble, {
  bubbleIcons,
  ChatbotBubbleOptions,
} from "@/components/dialog/ChatbotOpenBubble.js";
import { useQuery, useQueryClient } from "react-query";
import Textarea from "@/components/ui/Textarea";
import Switch from "@/components/ui/Switch";
import Button from "@/components/ui/Button.jsx";
import Textinput from "@/components/ui/Textinput";
import Select from "@/components/ui/Select";
import { components } from "react-select";
import ColorPicker from "@/components/ColorPicker";
import Card from "@/components/ui/Card.jsx";
import {Icon} from "@/components/Icon.tsx";
import { Tab } from "@headlessui/react";
import { contrastColor, hexToRGB } from "@/utils/colors";
import FileInputPreview from "@/components/FileInputPreview";
import "./styles.scss";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import {camelToSnakeObj, getTimestamp, resetSession, snakeToCamelObj} from "@/utils/utils";
import { toast } from "react-toastify";
import { queryKey } from "@/store/queryKeys";
import "@/_embed/styles.scss";
import {
  ChatBotDialogOptions,
  Msg,
  roleAssistant,
  roleUser,
} from "@/components/dialog/types";
import { useParams } from "react-router-dom";

import { PlanHobbyBadge, PlanProBadge } from "@/components/partials/planBadges";
import { useTranslation } from "react-i18next";
import {WidgetOptions} from "@/models/chatbot.ts";

//Lazy components
const Wysiwyg = lazy(() => import("@/pages/chatbot/design/Wysiwyg"));

let tabs: any[] = [
  { title: "chatbot_design.chat_widget", isHeader: true },
  { title: "chatbot_design.general", nested: 1 },
  { title: "chatbot_design.colors", nested: 1 },
  { title: "chatbot_design.icons", nested: 1 },
  { title: "chatbot_design.sources", nested: 1 },
  { title: "chatbot_design.bubble_button" },
  { title: "chatbot_design.behaviour" },

  { title: "chatbot_design.tabs_header", isHeader: true },
  { title: "chatbot_design.home_tab", nested: 1 },
];

let tabIdx = 0;
tabs = tabs.map((item) => {
  if (item.isHeader) {
    return item;
  }
  item = Object.assign(item, { tabIndex: tabIdx });
  tabIdx++;
  return item;
});

const bubbleDefaultBgColor = "#000000";
const bubbleDefaultIconColor = "#FFFFFF";

// @ts-ignore
const OptionComponent = ({ data, ...props }) => {
  return (
    /*@ts-ignore*/
    <components.Option {...props}>
      <div className="flex items-center">
        <Icon icon={data.icon} width="40" />
        <span className="ml-2">{data.label}</span>
      </div>
    </components.Option>
  );
};

const bubblePosition = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
];

const initDialogOptions = (
  widgetState: WidgetOptions,
  dialogCurrentState: ChatBotDialogOptions,
): ChatBotDialogOptions => {
  let initialMessages = widgetState.initialMessages.split("\n");
  let initialMessage =
    initialMessages[Math.floor(Math.random() * initialMessages.length)];

  let options = Object.assign({}, dialogCurrentState);
  options.initialMessage = initialMessage;
  return options;
};

const ChatbotDesign = () => {
  const { t } = useTranslation();
  const { chatbotID } = useParams();
  const queryKeyID = `${queryKey.chatbotInterfaceSettings}-${chatbotID}`;
  const queryClient = useQueryClient();
  let chatbotQuery = useQuery({
    queryKey: queryKeyID,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let res = await fetch(`${constants.url.API_PREFIX}chatbot/${chatbotID}`);
      return res.json();
    },
  });

  const [isDialogOpened, setDialogOpened] = React.useState(true);

  const [viewWidgetOptions, setWidgetOptions] = React.useState(
    null as null | WidgetOptions,
  );
  const [viewDialogOptions, setDialogOptions] = React.useState(
    null as null | ChatBotDialogOptions,
  );
  const [viewBubbleOptions, setBubbleOptions] = React.useState(
    null as null | ChatbotBubbleOptions,
  );

  const [bubbleSelectedFile, setBubbleSelectedFile] = React.useState(undefined);
  const [profileSelectedFile, setProfileSelectedFile] =
    React.useState(undefined);

  const saveSettings = async () => {
    let settingsObj: any = Object.assign({}, viewWidgetOptions);
    settingsObj.bubbleSettings = viewBubbleOptions;
    settingsObj.dialogSettings = viewDialogOptions;

    const formData = new FormData();
    formData.append(
      "widget_settings",
      JSON.stringify(camelToSnakeObj(settingsObj)),
    );

    if (bubbleSelectedFile) {
      formData.append("bubble_image", bubbleSelectedFile);
    }
    if (profileSelectedFile) {
      formData.append("profile_image", profileSelectedFile);
    }

    try {
      let res = await fetch(
        `${constants.url.API_PREFIX}chatbot/${chatbotID}/settings`,
        {
          method: "POST", // headers: { 'Content-Type': 'multipart/form-data' },
          body: formData,
        },
      );
      let json = await res.json();
      if (json.error) {
        toast.error(json.error, {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      } else if (json.message) {
        toast.success(json.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "light",
        });
      }
    } catch (e) {
      toast.error("Upload error", {
        position: "top-right",
        autoClose: 1500,
        theme: "light",
      });
    }

    queryClient.invalidateQueries(queryKeyID);
  };

  if (chatbotQuery.isFetched) {
    let data: any = snakeToCamelObj(chatbotQuery.data);
    if (!data.widgetSettings) {
      return <div>Loading...</div>;
    }

    // Initial values
    let qSettings: { [key: string]: any } = {};
    let qDialogOptions: { [key: string]: any } = {};
    let qBubbleOptions: { [key: string]: any } = {
      onClick: () => {
        setDialogOpened((state) => {
          return !state;
        });
      },
      opened: isDialogOpened,
    };

    // Split data for React components
    for (const [key, value] of Object.entries(data.widgetSettings)) {
      if (typeof value === "object") {
        if (key === "bubbleSettings") {
          Object.assign(qBubbleOptions, value);
        } // merge with initial values
        if (key === "dialogSettings") {
          Object.assign(qDialogOptions, value);
        }
      } else {
        qSettings[key] = value;
      }
    }

    if (!viewWidgetOptions || !viewDialogOptions || !viewDialogOptions) {
      setWidgetOptions(qSettings as WidgetOptions);
      setBubbleOptions(qBubbleOptions as ChatbotBubbleOptions);
      setDialogOptions(
        initDialogOptions(
          qSettings as WidgetOptions,
          qDialogOptions as ChatBotDialogOptions,
        ),
      );
    }
  }

  if (!viewWidgetOptions || !viewBubbleOptions || !viewDialogOptions) {
    return <div>Loading...</div>;
  }

  let timestamp = getTimestamp();
  const msgHistoryExample: Msg[] = [
    {
      id: 0,
      text: viewDialogOptions.initialMessage,
      role: roleAssistant,
      time: timestamp - 180,
    },
    {
      id: 1,
      text: "How to start building my chatbot?",
      role: roleUser,
      time: timestamp - 60,
    },
    {
      id: 2,
      historyId: "testID",
      text: "Great question! First of all, let's upload some data into a knowledge base 😉🚀",
      role: roleAssistant,
      sourceID: 0,
      sourcesExpanded: viewDialogOptions.sourcesAutoExpand,
      streaming: false,
      sources: [
        {
          title: "WizyChat | Custom GPT chatbots trained on your data",
          description:
            "Custom GPT chatbots trained on your data. Zero coding required. Share on your website in minutes.",
          url: "https://wizy.chat",
          image: "https://wizy.chat/images/og-twitter-banner.png",
          favicon: "https://wizy.chat/images/favicon.svg",
        },
        {
          title: "WizyChat | Pricing",
          description:
            "Pricing plans for any size business. 14-day money-back guarantee!",
          url: "https://wizy.chat/pricing",
          image: "https://wizy.chat/images/og-twitter-banner.png",
          favicon: "https://wizy.chat/images/favicon.svg",
        },
      ],
      time: timestamp,
    },
  ];

  // helpers
  let bubbleBgRGB = hexToRGB(viewBubbleOptions.iconBgColor);

  return (
    <div className="max-w-[1500px]">
      <Card title={t("sidebar.chat_interface")} bodyClass="p-2">
        <div className="design-settings flex flex-col 2xl:flex-row">
          <div className="2xl:border-r-2 lg:pr-2">
            <Tab.Group>
              <div className="flex flex-col lg:flex-row">
                <div className="lg:border-r-2 mr-6 h-full">
                  <Tab.List className="lg:w-[175px] pr-1 grid grid-cols-4 lg:grid-cols-1 gap-0">
                    {tabs.map((item, i) => {
                      if (item.isHeader) {
                        return (
                          <div
                            key={`header-${i}`}
                            className="pl-1 p-3 font-semibold hidden lg:block"
                          >
                            {t(item.title)}
                          </div>
                        );
                      } else {
                        return (
                          <Tab key={item.tabIndex} as={Fragment}>
                            {({ selected }) => (
                              <button
                                className={` text-sm text-left font-medium md:block capitalize ring-0 foucs:ring-0 focus:outline-none px-3 rounded-md py-2 transition ${
                                  item.nested ? `lg:ml-3 ` : ""
                                } ${
                                  selected
                                    ? "text-white bg-primary-500 "
                                    : "text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300"
                                }`}
                              >
                                {t(item.title)}
                              </button>
                            )}
                          </Tab>
                        );
                      }
                    })}
                  </Tab.List>
                </div>
                <div className="w-full min-w-[400px]">
                  <Tab.Panels>
                    <Tab.Panel>
                      <div className="mt-2">
                        {/*@ts-ignore*/}
                        <Textinput
                          label={t("chatbot_design.header")}
                          type="text"
                          placeholder=""
                          defaultValue={viewDialogOptions.title}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.title = e.target.value;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        {/*@ts-ignore*/}
                        <Textarea
                          label={t("chatbot_design.initial_message")}
                          placeholder=""
                          row={3}
                          value={viewWidgetOptions.initialMessages}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewWidgetOptions);
                            options.initialMessages = e.target.value;
                            setWidgetOptions(options);
                          }}
                        />
                        <div className="text-xs mt-1 opacity-70">
                          {t("chatbot_design.initial_message_desc")}
                        </div>
                      </div>

                      <div className="mt-2">
                        {/*@ts-ignore*/}
                        <Textarea
                          label={t("chatbot_design.quick_questions_buttons")}
                          placeholder=""
                          row={3}
                          value={viewDialogOptions.initialQuestions}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.initialQuestions = e.target.value;
                            setDialogOptions(options);
                          }}
                        />
                        <div className="text-xs text-gray-400 mt-1">
                          {t("chatbot_design.initial_message_desc")}
                        </div>
                      </div>

                      <div className="mt-2">
                        <Switch
                          label={<span>{t("chatbot_design.voice_to_text")}{PlanHobbyBadge}</span>}
                          value={viewDialogOptions.voiceToText}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.voiceToText = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        <Switch
                          label={
                            <span>{t("chatbot_design.remove_powered_by")}{PlanProBadge}</span>
                          }
                          value={viewDialogOptions.hidePoweredBy}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.hidePoweredBy = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-3">
                        <Switch
                          label={t("chatbot_design.show_online_status")}
                          value={viewDialogOptions.showOnline}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.showOnline = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-2">
                        <label className="block capitalize form-label  ">
                          {t("chatbot_design.widget_primary_color")}
                        </label>
                        <ColorPicker
                          color={viewDialogOptions.profileIconBgColor}
                          defaultColor="#6874E3"
                          onChange={(color: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.profileIconBgColor = color.hex;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        <label className="block capitalize form-label  ">
                          {t("chatbot_design.assistant_message_color")}
                        </label>
                        <ColorPicker
                          color={viewDialogOptions.msgAssistantColor}
                          defaultColor="#818CF8"
                          onChange={(color: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.msgAssistantColor = color.hex;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        <label className="block capitalize form-label">
                          {t("chatbot_design.user_message_color")}
                        </label>
                        <ColorPicker
                          color={viewDialogOptions.msgUserColor}
                          defaultColor="#E0E7FF"
                          onChange={(color: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.msgUserColor = color.hex;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-2">
                        <label className="block capitalize form-label  ">
                          {t("chatbot_design.assistant_icon")}
                        </label>
                        <div className="grid grid-cols-4 gap-3 gap-x-3">
                          {Object.entries(botIcons()).map(
                            (item: any, i: number) => {
                              return (
                                <div
                                  key={i}
                                  className={
                                    "w-12 h-12 p-1 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center border-2 cursor-pointer mr-2 " +
                                    (item[0] == viewDialogOptions.profileIcon
                                      ? "border-primary-300"
                                      : "")
                                  }
                                  onClick={() => {
                                    let options = Object.assign(
                                      {},
                                      viewDialogOptions,
                                    );
                                    options.profileIcon = item[0];
                                    setDialogOptions(options);
                                  }}
                                >
                                  {item[1].icon}
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>

                      <FileInputPreview
                        label={t("chatbot_design.assistant_image")}
                        file={
                          profileSelectedFile
                            ? profileSelectedFile
                            : viewDialogOptions.profileImg
                            ? ({
                                name: viewDialogOptions.profileImg
                                  .split("/")
                                  .pop(),
                              } as any)
                            : null
                        }
                        className="mt-2"
                        onChange={(e) => {
                          setProfileSelectedFile(e.file as any);
                          let options = Object.assign({}, viewDialogOptions);
                          options.profileImg = e.previewURL;
                          if (!e.file) {
                            options.profileIcon = "1";
                          }
                          setDialogOptions(options);
                        }}
                      />

                      <div className="mt-6">
                        <Switch
                          label={t("chatbot_design.show_icon_header")}
                          value={viewDialogOptions.showProfileIconHeader}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.showProfileIconHeader = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        <Switch
                          label={t("chatbot_design.show_icon_messages")}
                          value={viewDialogOptions.showProfileIconMessage}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.showProfileIconMessage = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-6">
                        <Switch
                          label={t("chatbot_design.show_sources_response")}
                          value={viewDialogOptions.showResponseWebLinks}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.showResponseWebLinks = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>

                      <div className="mt-2">
                        <Switch
                          label={t("chatbot_design.auto_show_sources")}
                          value={viewDialogOptions.sourcesAutoExpand}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.sourcesAutoExpand = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-2">
                        {/*@ts-ignore*/}
                        <Select
                          label={t("chatbot_design.button_position")}
                          options={bubblePosition}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewBubbleOptions);
                            options.position = e.target.value;
                            setBubbleOptions(options);
                          }}
                          value={viewBubbleOptions.position}
                        />
                      </div>

                      <FileInputPreview
                        label={t("chatbot_design.button_custom_icon")}
                        file={
                          bubbleSelectedFile
                            ? bubbleSelectedFile
                            : viewBubbleOptions.image
                            ? ({
                                name: viewBubbleOptions.image.split("/").pop(),
                              } as any)
                            : null
                        }
                        className="mt-2"
                        onChange={(e) => {
                          setBubbleSelectedFile(e.file as any);
                          let options = Object.assign({}, viewBubbleOptions);
                          options.image = e.previewURL;
                          if (!e.file) {
                            options.icon = "1";
                          }
                          setBubbleOptions(options);
                        }}
                      />

                      <div className="mt-2">
                        <label className="block capitalize form-label  ">
                          {t("chatbot_design.button_icon")}
                        </label>
                        <div className="flex">
                          {Object.entries(bubbleIcons).map(
                            (item: any, i: number) => {
                              return (
                                <div
                                  key={i}
                                  className={
                                    "w-12 h-12 p-1 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center border-2 cursor-pointer mr-2 " +
                                    (item[0] == viewBubbleOptions.icon
                                      ? "border-primary-300"
                                      : "")
                                  }
                                  onClick={() => {
                                    let options = Object.assign(
                                      {},
                                      viewBubbleOptions,
                                    );
                                    options.icon = item[0];
                                    if (options.icon !== "custom") {
                                      setBubbleSelectedFile(undefined);
                                    }
                                    setBubbleOptions(options);
                                  }}
                                >
                                  {item[1].icon}
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>

                      {!bubbleSelectedFile &&
                        viewBubbleOptions.icon !== "custom" && (
                          <div>
                            <div className="mt-2">
                              <label className="block capitalize form-label  ">
                                {t("chatbot_design.button_bg_color")}
                              </label>
                              <ColorPicker
                                color={viewBubbleOptions.iconBgColor}
                                defaultColor={bubbleDefaultBgColor}
                                onChange={(color: any) => {
                                  let options = Object.assign(
                                    {},
                                    viewBubbleOptions,
                                  );
                                  options.iconBgColor = color.hex;
                                  options.iconColor = contrastColor(
                                    color.rgb.r,
                                    color.rgb.g,
                                    color.rgb.b,
                                  );
                                  setBubbleOptions(options);
                                }}
                              />
                            </div>

                            <div className="mt-2">
                              <label className="block capitalize form-label  ">
                                {t("chatbot_design.button_icon_color")}
                              </label>
                              <ColorPicker
                                color={viewBubbleOptions.iconColor}
                                defaultColor={bubbleDefaultIconColor}
                                onChange={(color: any) => {
                                  let options = Object.assign(
                                    {},
                                    viewBubbleOptions,
                                  );
                                  options.iconColor = color.hex;
                                  setBubbleOptions(options);
                                }}
                              />
                            </div>
                          </div>
                        )}
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-2">
                        <Switch
                          label={t("chatbot_design.button_show_initial_message")}
                          value={viewWidgetOptions.showQuestionsOutside}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewWidgetOptions);
                            options.showQuestionsOutside = e.target.checked;
                            if (options.showQuestionsOutside) {
                              setDialogOpened(false);
                            }
                            setWidgetOptions(options);
                          }}
                        />
                      </div>

                      <div className="py-3">
                        <label
                          className="block capitalize form-label text"
                          style={{ textTransform: "unset" }}
                        >
                          {`${t("chatbot_design.button_show_initial_message_sec")}: ${viewWidgetOptions.initialMessagesAfter} ${t("sec")}`}
                        </label>
                        <div className="px-2">
                          <Slider
                            min={0}
                            max={60}
                            step={1}
                            value={viewWidgetOptions.initialMessagesAfter}
                            onChange={(val) => {
                              let options = Object.assign(
                                {},
                                viewWidgetOptions,
                              );
                              options.initialMessagesAfter = val as number;
                              setWidgetOptions(options);
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1 px-2">
                          <span>{t("immediately")}</span>
                          <span>{`60${t("sec")}`}</span>
                        </div>
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-3">
                        <Switch
                          label={t("chatbot_design.show_home_tab")}
                          value={viewDialogOptions.tabHomeShow}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.tabHomeShow = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                      <div className="mt-3">
                        <Switch
                          label={t("chatbot_design.home_tab_icon")}
                          value={viewDialogOptions.tabHomeShowIcon}
                          onChange={(e: any) => {
                            let options = Object.assign({}, viewDialogOptions);
                            options.tabHomeShowIcon = e.target.checked;
                            setDialogOptions(options);
                          }}
                        />
                      </div>
                      <Wysiwyg
                        className="mt-3"
                        html={viewDialogOptions.tabHomeHtml}
                        onChange={(html: string) => {
                          let options = Object.assign({}, viewDialogOptions);
                          options.tabHomeHtml = html;
                          setDialogOptions(options);
                        }}
                      />
                    </Tab.Panel>
                  </Tab.Panels>
                </div>
              </div>
            </Tab.Group>

            <div className="flex justify-end py-6">
              {/*@ts-ignore*/}
              <Button
                text={t("save_changes")}
                className="btn-primary"
                onClick={saveSettings}
              />
            </div>
          </div>
          <div
            id="wizychat-container"
            className="w-full p-4 flex justify-end flex-col !relative min-h-[80vh] dots"
          >
            <div
              className="flex flex-col"
              style={
                viewBubbleOptions.position === "right"
                  ? { alignItems: "flex-end", right: "1.5rem" }
                  : { alignItems: "flex-start", left: "1.5rem" }
              }
            >
              {chatbotQuery.isSuccess &&
                isDialogOpened &&
                viewDialogOptions && (
                  <div
                    className={
                      "mb-6 absolute min-w-[350px] lg:w-3/4 h-[60vh] bottom-20 shadow-2xl " +
                      (viewBubbleOptions.position === "right"
                        ? "right-4"
                        : "left-4")
                    }
                  >
                    <ChatbotDialog
                      messages={msgHistoryExample} // will show "example messages"
                      token={chatbotQuery.data.token}
                      hideCloseBtn={false}
                      design={viewDialogOptions}
                      onClose={() => {
                        setDialogOpened(false);
                      }}
                      onRefresh={() => {
                        resetSession(chatbotQuery.data.token);
                        setDialogOpened(false);
                        setDialogOptions(
                          initDialogOptions(
                            viewWidgetOptions,
                            viewDialogOptions,
                          ),
                        );
                        setTimeout(() => {
                          setDialogOpened(true);
                        }, 200);
                      }}
                    />
                  </div>
                )}

              {/*Initial question outside*/}
              {viewWidgetOptions.showQuestionsOutside && !isDialogOpened && (
                <div className="wz-question-outside-wrapper">
                  <div
                    className="wz-question-outside"
                    style={{
                      backgroundColor: viewBubbleOptions.iconBgColor,
                      color: contrastColor(
                        bubbleBgRGB.r,
                        bubbleBgRGB.g,
                        bubbleBgRGB.b,
                      ),
                    }}
                    onClick={() => {
                      setDialogOpened((state) => {
                        return !state;
                      });
                    }}
                  >
                    {viewDialogOptions.initialMessage}
                  </div>
                </div>
              )}

              <ChatbotOpenBubble
                {...viewBubbleOptions}
                opened={isDialogOpened}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatbotDesign;
